'use client';

import { ContactUsContainer, SimpleCard } from '@/modules/cms';
import type { AboutWasticPage, ContactUsPage } from '@/modules/sanity-studio/types';
import {
	For,
	SimpleGrid,
	Stack,
	Stat,
	Text,
} from '@chakra-ui/react';
import { DefaultStyle } from '@packages/wastic-shared';

import { useLocale } from 'next-intl';

type AboutWasticContainerProps = {
	aboutWasticPage: AboutWasticPage;
	contactUsPage:   ContactUsPage;
};

const AboutWasticContainer = ({ aboutWasticPage, contactUsPage }: AboutWasticContainerProps) => {
	const locale = useLocale() as 'en' | 'th';

	return (
		<Stack gap={DefaultStyle.CONTENT_GAP} layerStyle={'black'}>
			<Stack gap={DefaultStyle.SECTION_GAP}>
				<Stack alignItems={'center'} gap={DefaultStyle.NO_GAP} >
					<Text as={'h2'} textAlign={'center'} textStyle={'h2'}>{aboutWasticPage.heading.title[locale]}</Text>
					<Text
						maxW={'80ch'}
						px={8}
						textAlign={'center'}
						textStyle={'p3'}
					>
						{aboutWasticPage.heading.description[locale]}
					</Text>
				</Stack>
				<Stack
					bg={'black'}
					borderRadius={12}
					color={'white'}
					gap={DefaultStyle.SECTION_GAP}
					p={12}
				>
					<Text
						as={'h3'}
						textAlign={'center'}
						textStyle={'h4'}
						textTransform={'uppercase'}
					>
						{aboutWasticPage.impact.title[locale]}
					</Text>
					<SimpleGrid
						alignContent={'center'}
						columnGap={DefaultStyle.SECTION_GAP}
						columns={{
							base: 1,
							xl:   2,
						}}
						rowGap={DefaultStyle.TIGHT_SECTION_GAP}
					>
						<For each={aboutWasticPage.impact.impacts}>
							{(impact, index) => (
								<Stat.Root key={`${impact.heading?.[locale]} ${index}`} alignItems={'center'}>
									<Stat.Label color={'white'}>
										<Text textStyle={'h2'}>{impact.heading[locale]}</Text>
									</Stat.Label>
									<Stat.ValueText color={'whiteAlpha.700'}>
										<Text textAlign={'center'} textStyle={'p2'}>{impact.description[locale]}</Text>
									</Stat.ValueText>
								</Stat.Root>
							)}
						</For>
					</SimpleGrid>
				</Stack>
			</Stack>
			<Stack gap={DefaultStyle.MEDIUM_GAP}>
				<Text as={'h3'} textStyle={'h4'}>{aboutWasticPage.materials.title[locale]}</Text>
				<SimpleGrid
					columns={{
						base: 1,
						lg:   3,
					}}
					gap={DefaultStyle.MEDIUM_GAP}
				>
					<For each={aboutWasticPage.materials.materials}>
						{(material, index) => (
							<SimpleCard
								key={`${material.heading?.[locale]} ${index}`}
								description={material.description[locale]}
								heading={material.heading[locale] ?? ''}
								image={material.image?.asset.url ?? ''}
							/>
						)}
					</For>
				</SimpleGrid>
			</Stack>
			<ContactUsContainer contactUsPage={contactUsPage} />
		</Stack>
	);
};

export default AboutWasticContainer;
